import { Stack, Typography, useTheme } from "@mui/material";
import { TrendingDown, TrendingUp } from "lucide-react";
import { type FC, type ReactNode, useState } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { ChartInfoTooltip } from "web_myplann/Portfolio/Components/Redesign/Detail/ChartInfoTooltip";
import { ChartTimeFrameTabs } from "web_myplann/Portfolio/Components/Redesign/Detail/ChartTimeFrameTabs";
import { ChartTooltip } from "web_myplann/Portfolio/Components/Redesign/Detail/ChartTooltip";
import { getChartValueMetrics } from "web_myplann/Portfolio/Components/Redesign/Detail/getChartValueMetrics";
import { useChartSeries } from "web_myplann/Portfolio/Components/Redesign/Detail/useChartSeries";
import type { DayData } from "web_myplann/Portfolio/Helpers/calculateGraphData";
import { PercentageDelta } from "web_myplann/Shared/Components/PercentageDelta";
import { MyPlannLineChart } from "web_myplann/Shared/Components/Redesign/MyPlannLineChart";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";
import type { ChartTimeFrameKey } from "web_myplann/Utils/types";

const DEFAULT_TIME_FRAME: ChartTimeFrameKey = 9999;

type Props = {
	totalValue: number;
	totalInvestedValue: number;
	totalEvaluationPercent: number;
	daysData: DayData[];
	currencySign: string;
	toDate: string;
	loanChart?: boolean;
	showChartInfo?: boolean;
	bottomComponent?: ReactNode;
};

export const DetailValueChart: FC<Props> = ({
	totalValue,
	totalInvestedValue,
	totalEvaluationPercent,
	daysData,
	currencySign,
	toDate,
	loanChart,
	showChartInfo,
	bottomComponent,
}) => {
	const { t } = useResource();
	const theme = useTheme();

	const [timeFrame, setTimeFrame] =
		useState<ChartTimeFrameKey>(DEFAULT_TIME_FRAME);

	const {
		investmentResult,
		evaluationPercent,
		isPositive,
		isZeroInvestmentRatio,
	} = getChartValueMetrics({
		totalValue,
		totalInvestedValue,
		totalEvaluationPercent,
	});

	const {
		isEmpty,
		dates,
		values,
		invested,
		minDate,
		maxDate,
		xValueFormatter,
		yValueFormatter,
		seriesValueFormatter,
	} = useChartSeries({ daysData, timeFrame, currencySign });

	if (isEmpty) {
		return null;
	}

	const chartLabel = loanChart
		? t(Resources.Portfolio.Chart.RemainsToPay)
		: t(Resources.Portfolio.Chart.Value);

	const yLabel = loanChart
		? t(Resources.Portfolio.Chart.Paid)
		: t(Resources.Portfolio.Chart.Invested);

	const deltaColor = isZeroInvestmentRatio
		? theme.palette.grey[500]
		: isPositive
			? theme.palette.green[600]
			: theme.palette.red[600];

	const tickLabelStyle = {
		fontSize: 10,
		fontWeight: 400,
		fill: theme.palette.grey[400],
	};

	return (
		<Stack bgcolor={theme.palette.background.paper} pt={theme.spacingTokens.xl}>
			<Stack
				direction="row"
				gap={0.6}
				alignItems="center"
				mb={theme.spacingTokens.xs}
				px={theme.spacingTokens.lg}
			>
				<Typography variant="body2" color={theme.palette.grey[600]}>
					{chartLabel}
				</Typography>
				<ChartInfoTooltip title={t(Resources.Portfolio.Chart.Tooltip)} />
			</Stack>

			<Typography
				variant="displayMd"
				lineHeight={1.25}
				tabular
				px={theme.spacingTokens.lg}
			>
				{moneyFormatter(totalValue, currencySign, "", 2)}
			</Typography>

			{!loanChart && showChartInfo && (
				<Stack
					direction="row"
					spacing={theme.spacingTokens.sm}
					alignItems="center"
					mt={theme.spacingTokens.xs}
					px={theme.spacingTokens.lg}
				>
					{!isZeroInvestmentRatio &&
						(isPositive ? (
							<TrendingUp size={theme.iconSize.xs} color={deltaColor} />
						) : (
							<TrendingDown size={theme.iconSize.xs} color={deltaColor} />
						))}
					<Typography variant="body2" color={deltaColor} tabular>
						{moneyFormatter(
							investmentResult,
							currencySign,
							`0 ${currencySign}`,
							2,
						)}
					</Typography>
					<PercentageDelta
						change={evaluationPercent}
						textBefore="("
						textAfter=")"
						color={deltaColor}
					/>
					<Typography variant="body2" color={theme.palette.grey[400]} tabular>
						{toDate}
					</Typography>
				</Stack>
			)}

			{loanChart && (
				<Typography
					variant="body2"
					color={theme.palette.grey[400]}
					mt={theme.spacingTokens.xs}
					px={theme.spacingTokens.lg}
					tabular
				>
					{t(Resources.Portfolio.Chart.To)} {toDate}
				</Typography>
			)}

			{dates && values && invested && (
				<Stack pr={theme.spacingTokens.lg}>
					<MyPlannLineChart
						xAxis={[
							{
								scaleType: "time",
								data: dates,
								disableTicks: true,
								valueFormatter: xValueFormatter,
								tickLabelStyle,
								min: minDate,
								max: maxDate,
							},
						]}
						yAxis={[
							{
								position: "left",
								disableLine: true,
								disableTicks: true,
								valueFormatter: yValueFormatter,
								tickLabelStyle,
							},
						]}
						grid={{ horizontal: true, vertical: true }}
						series={[
							{
								data: invested,
								color: theme.palette.grey[300],
								label: yLabel,
								showMark: false,
								disableHighlight: loanChart,
								valueFormatter: seriesValueFormatter,
							},
							{
								data: values,
								color: theme.palette.blue[500],
								label: chartLabel,
								showMark: false,
								valueFormatter: seriesValueFormatter,
							},
						]}
						height={180}
						margin={{ top: 10, right: 0, bottom: 5, left: 0 }}
						hideLegend
						skipAnimation
						slots={{ tooltip: ChartTooltip }}
						hideFirstSeriesLine={loanChart}
					/>
				</Stack>
			)}

			<ChartTimeFrameTabs value={timeFrame} onChange={setTimeFrame} />

			{bottomComponent && (
				<Stack pt={theme.spacingTokens.sm}>{bottomComponent}</Stack>
			)}
		</Stack>
	);
};
